import React, {useEffect, useMemo, useState} from 'react';
import {Table, Button, Tag, Row, Divider, Col, Popover} from 'antd';
import {EyeOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {
  PageSizeArray,
  PaginationPosition,
  UserStatusOptions,
  WillReceiveUpdateOptions,
  UserRoleEnums,
  userFilters,
  ReturnNAOnEmpty,
  formatCurrencyByLocalChapter,
} from '../../../Util/commonUtility';
import DynamicSearchForm from '../../CommonComponents/DynamicSearch';
import {CSVLink} from 'react-csv';
import StatusUpdateModal from '../../CommonComponents/CommonModal';
import {useDispatch, useSelector} from 'react-redux';
import AdminActionsDropdown from './../../CommonComponents/AdminActionsDropdown/AdminActionsDropdown';
import useDebouncedCallback from '../../../hooks/useDebounce';
import {DescriptionItem} from './../../../Util/commonUtility';
import AdminViewDetailsDrawer from '../../CommonComponents/AdminViewDetailsDrawer';
import CommonTableColumnTextView from './../../CommonComponents/CommonTableColumnTextView/index';
import {getAllChapters} from '../../../Store/Actions';
import dayjs from 'dayjs';
import HeaderSearchCard from '../../CommonComponents/HeaderSearchCard';
import {CREATE} from '../../../Constant/enums';
import useLocalization from '../../../Localization';
import {ProtectedResource} from '../../CommonComponents/ProtectedResource';
import CreditHistoryModal from '../../CommonComponents/CreditHistoryModal';

const UserComponent = (props) => {
  let navigate = useNavigate();
  const {ApplicationString} = useLocalization();

  const {
    allUsersData,
    paging,
    chaptersOptions,
    handleTableChange,
    fetchUsers,
    fetchUsersWithoutPagination,
    handlePaginationChange,
    deleteUser,
    sortParams,
    allUsersDataWithoutPagination,
    updateStatusById,
    updateWillReceiveById,
    searchValues,
    csvDataLoading,
  } = props;
  const ps = useSelector(({pageSizes}) => pageSizes.pageSizes.users);
  const [expanded, setExpanded] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [recordData, setRecordData] = useState();
  const [isWillReceiveModalVisible, setIsWillReceiveModalVisible] =
    useState(false);
  const [showWillReceiveSelection, setShowWillReceiveSelection] =
    useState('yes');
  const [recordDataWillReceive, setRecordDataWillReceive] = useState();
  const [open, setOpen] = useState(false);
  const [openDrawerDetails, setOpenDrawerDetails] = useState(false);
  const [userChapterData, setUserChapterData] = useState();
  const [creditHistoryOpen, setCreditHistoryOpen] = useState(false);
  const [creditHistoryUser, setCreditHistoryUser] = useState(null);
  const dispatch = useDispatch();
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const loggedUserRole = userInfo?.role;
  const localUserId = userInfo?.id;
  const userRoleData = allUsersData?.filter(
    (obj) => obj.role === UserRoleEnums.USER,
  );
  const filteredAdminAndUserData = allUsersData?.filter(
    (obj) => obj.role !== UserRoleEnums.SUPER_ADMIN,
  );
  let canEdit = false;
  let canDelete = false;
  let canUpdateStatus = false;
  const allChapters = useSelector((state) => state.chapters.allChapters);

  useEffect(() => {
    searchValues && setExpanded(true);
  }, [searchValues]);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };
  const handleEditClick = async (id) => {
    navigate(`/admin/memberAttendees/update/${id}`);
  };
  const handleSearch = (values) => {
    fetchUsers(values);
  };
  const handleUpdateStatusClick = async (record) => {
    setIsModalVisible(true);
    const statusMap = {
      1: 'active',
      0: 'in-active',
    };
    const status = statusMap[record.userstatus] || record.userstatus;
    setShowUpdateStatus(status);
    setRecordData(record);
  };

  const handleChange = (value) => {
    setShowUpdateStatus(value);
  };

  const handleUpdateJobStatus = () => {
    updateStatusById(recordData, showUpdateStatus);
    hideModal(false);
    setShowUpdateStatus(false);
  };

  const hideModal = () => {
    setIsModalVisible(false);
  };

  const handleUpdateWillReceiveClick = async (record) => {
    setIsWillReceiveModalVisible(true);
    setShowWillReceiveSelection(
      record?.will_receive_update !== false ? 'yes' : 'no',
    );
    setRecordDataWillReceive(record);
  };

  const handleWillReceiveChange = (value) => {
    setShowWillReceiveSelection(value);
  };

  const hideWillReceiveModal = () => {
    setIsWillReceiveModalVisible(false);
  };

  const handleUpdateWillReceiveSubmit = () => {
    updateWillReceiveById(
      recordDataWillReceive,
      showWillReceiveSelection === 'yes',
    );
    hideWillReceiveModal();
    setShowWillReceiveSelection('yes');
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  // Drawer functions ----------------------------------------------------
  const showDrawer = (record) => {
    setOpenDrawerDetails(record);
    setOpen(true);
  };

  const onCloseDrawer = () => {
    setOpen(false);
  };

  // Registration history for a single member (opens the dedicated admin screen, scoped by user_id).
  const handleRegistrationHistoryClick = (record) => {
    navigate(`/admin/registrationHistory?user_id=${record.id}`);
  };

  // Per-member credit history modal.
  const handleCreditHistoryClick = (record) => {
    setCreditHistoryUser({
      id: record.id,
      name: `${record.first_name || ''} ${record.last_name || ''}`.trim(),
      chapter: allChapters?.data?.chapters?.find(
        (obj) => obj.id === record?.local_chapter_id?.id,
      ),
    });
    setCreditHistoryOpen(true);
  };

  useEffect(() => {
    if (!userChapterData) {
      dispatch(getAllChapters());
    }
  }, [userChapterData]);

  const MemberAttendeesDrawerContent = () => {
    const {
      first_name,
      last_name,
      company,
      email,
      phone_number,
      city,
      state,
      country,
      address,
      postal_code,
      fax_number,
      local_chapter_id,
      userstatus,
      mail_preference,
      termsacceptedtime,
      refund_credit,
      notes,
      keywords,
      role,
      title,
      area_of_interest,
      last_login,
      is_vendor_reps,
      istermsaccepted,
      join_date,
      will_receive_update,
    } = openDrawerDetails || {};

    const renderList = (items) => {
      if (Array.isArray(items)) {
        const listItems = items.map((item, index) => (
          <span key={item?.name || index}>
            {`${item?.name || item}${index === items.length - 1 ? '' : ', '}`}
          </span>
        ));
        return items.length > 0 ? listItems : 'N/A';
      } else if (typeof items === 'boolean') {
        return items ? 'Yes' : 'No';
      }
    };

    return (
      <>
        <Row>
          <Col span={12}>
            <DescriptionItem title='First Name' content={first_name} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='Last Name' content={last_name} />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Total Refund Credit'
              content={formatCurrencyByLocalChapter(
                refund_credit,
                userChapterData,
                2,
                2,
              )}
            />
          </Col>
        </Row>
        <Divider />

        <Row>
          <Col span={12}>
            <DescriptionItem title='Email' content={email} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='Company / Organization' content={company} />
          </Col>
        </Row>
        <Divider />

        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Local Chapter'
              content={local_chapter_id?.name}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Selected Chapter Preference For PBSS Announcement'
              content={renderList(mail_preference)}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Will receive email updates'
              content={will_receive_update !== false ? 'Yes' : 'No'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Terms Accepted'
              content={istermsaccepted ? 'Yes' : 'No'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Terms Accepted Time'
              content={
                termsacceptedtime
                  ? dayjs(termsacceptedtime).format('YYYY-MM-DD hh:mm A')
                  : 'N/A'
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Selected Area of Interest'
              content={renderList(keywords)}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Other Area of Interest'
              content={area_of_interest || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />

        <Row>
          <Col span={12}>
            <DescriptionItem
              title='Phone Number'
              content={phone_number || 'N/A'}
            />
          </Col>
        </Row>
        <Divider />

        <Row>
          <Col span={24}>
            <DescriptionItem title='Address' content={address} />
          </Col>
        </Row>
        <Divider />

        <Row>
          <Col span={12}>
            <DescriptionItem title='City' content={city} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='State' content={state} />
          </Col>
        </Row>
        <Divider />

        <Row>
          <Col span={12}>
            <DescriptionItem title='Country' content={country} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='Postal Code' content={postal_code} />
          </Col>
        </Row>
        <Divider />

        <Row>
          <Col span={12}>
            <DescriptionItem title='Notes' content={notes || 'N/A'} />
          </Col>
          <Col span={12}>
            <DescriptionItem title='Job Title' content={title || 'N/A'} />
          </Col>
        </Row>
        <Divider />

        <Row>
          <Col span={12}>
            <DescriptionItem title='Role' content={role} />
          </Col>
          <Col span={12}>
            <DescriptionItem
              title='User Status'
              content={
                {
                  1: 'Active',
                  0: 'In-Active',
                }[userstatus] || userstatus
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Vendor Representative'
              content={is_vendor_reps ? 'Yes' : 'No'}
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='User Last Login'
              content={
                <span>
                  {last_login
                    ? dayjs(last_login).format('YYYY-MM-DD hh:mm A')
                    : 'NA'}
                </span>
              }
            />
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <DescriptionItem
              title='Join Date'
              content={
                join_date
                  ? dayjs(join_date).format('YYYY-MM-DD hh:mm A')
                  : 'N/A'
              }
            />
          </Col>
        </Row>
        <Divider />
      </>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '2%',
      sorter: true,
    },
    {
      title: 'First Name',
      dataIndex: 'first_name',
      key: 'first_name',
      width: '4%',
      sorter: true,
    },
    {
      title: 'Last Name',
      dataIndex: 'last_name',
      key: 'last_name',
      width: '4%',
      sorter: true,
    },
    {
      title: 'Company / Organization',
      dataIndex: 'company',
      key: 'company',
      width: '4%',
      sorter: true,
      render: (text, record) => {
        return <div>{ReturnNAOnEmpty(text)}</div>;
      },
    },
    {
      title: 'Local Chapter',
      dataIndex: ['local_chapter_id', 'name'],
      key: 'local_chapter_id',
      width: '4%',
      render: (text, record) => {
        return <span>{record?.local_chapter_id?.name || 'N/A'}</span>;
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '6%',
      sorter: true,
    },

    {
      title: 'Status',
      dataIndex: 'userstatus',
      key: 'status',
      width: '3%',
      render: (text, record) => {
        const statusMap = {
          1: 'Active',
          0: 'In-Active',
        };
        return <span>{statusMap[text]}</span>;
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: '2%',
      render: (text, record) => {
        return <span>{text}</span>;
      },
    },
    {
      title: 'Join Date',
      dataIndex: 'join_date',
      key: 'join_date',
      width: '4%',
      sorter: true,
      render: (text, record) => (
        <span>{text ? dayjs(text).format('YYYY-MM-DD hh:mm A') : 'NA'}</span>
      ),
    },
    {
      title: 'Terms Accepted',
      dataIndex: 'istermsaccepted',
      key: 'istermsaccepted',
      width: '2%',
      render: (text, record) => {
        return <span>{text ? 'Yes' : 'No'}</span>;
      },
    },
    {
      title: 'Last LogIn',
      dataIndex: 'last_login',
      key: 'last_login',
      width: '3%',
      render: (text, record) => {
        return (
          <span>{text ? dayjs(text).format('YYYY-MM-DD hh:mm A') : 'NA'}</span>
        );
      },
    },
    {
      title: 'Announcement Emails Last Opened',
      dataIndex: 'last_email_opened_at',
      key: 'last_email_opened_at',
      width: '4%',
      sorter: true,
      render: (text) => {
        return (
          <span>{text ? dayjs(text).format('YYYY-MM-DD hh:mm A') : 'NA'}</span>
        );
      },
    },
    {
      title: 'Announcement Emails Last Clicked',
      dataIndex: 'last_email_clicked_at',
      key: 'last_email_clicked_at',
      width: '4%',
      sorter: true,
      render: (text) => {
        return (
          <span>{text ? dayjs(text).format('YYYY-MM-DD hh:mm A') : 'NA'}</span>
        );
      },
    },
    {
      title: 'Receive Emails',
      dataIndex: 'will_receive_update',
      key: 'will_receive_update',
      width: '3%',
      render: (text, record) => (
        <span>{record?.will_receive_update !== false ? 'Yes' : 'No'}</span>
      ),
    },
    {
      title: 'Mail Preference',
      dataIndex: 'mail_preference',
      key: 'mail_preference',
      width: '3%',
      render: (text, record) => {
        return text?.length > 0 ? (
          <div className='fixed-size-text'>
            <Popover
              prefixCls='extend-popover'
              content={text?.map((item, index) => {
                return (
                  <Tag color='geekblue' key={index}>
                    {item?.name}
                  </Tag>
                );
              })}
              trigger='hover'
              title='Mail Preference'
              arrow={false}>
              <Button type='text' prefixCls='common-button-popover'>
                <EyeOutlined /> View{' '}
              </Button>
            </Popover>{' '}
          </div>
        ) : (
          'NA'
        );
      },
    },
    {
      title: 'Terms Accepted Time',
      dataIndex: 'termsacceptedtime',
      key: 'termsacceptedtime',
      width: '4%',
      render: (text, record) =>
        record.termsacceptedtime
          ? dayjs(record.termsacceptedtime).format('YYYY-MM-DD hh:mm A')
          : 'NA',
    },
    {
      title: 'Job Title',
      dataIndex: 'title',
      key: 'title',
      width: '4%',
      render: (text) => <div>{ReturnNAOnEmpty(text)}</div>,
    },
    {
      title: 'Refund Credit',
      dataIndex: 'refund_credit',
      width: '5%',
      fixed: 'right',
      render: (text, record) => {
        const localChapterData = allChapters?.data?.chapters?.find(
          (obj) => obj.id === record?.local_chapter_id?.id,
        );
        setUserChapterData(localChapterData);
        const formatted = formatCurrencyByLocalChapter(
          text,
          localChapterData,
          2,
          2,
        );
        // Always clickable — opens the credit history modal so an admin can add credit even
        // when the member currently has a zero balance and no prior credit activity.
        // Rendered as a styled span (not a Button) so it aligns exactly like the plain values.
        return (
          <span
            role='button'
            tabIndex={0}
            onClick={() => handleCreditHistoryClick(record)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleCreditHistoryClick(record);
              }
            }}
            style={{
              color: '#1677ff',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            id={`Users-credit-history-${record.id}`}>
            {formatted}
          </span>
        );
      },
    },
    {
      title: '',
      key: 'action',
      width: '2%',
      fixed: 'right',
      render: (_, record) => {
        if (
          loggedUserRole === UserRoleEnums?.SUPER_ADMIN &&
          record?.id === localUserId
        ) {
          canEdit = true;
        } else if (
          loggedUserRole === UserRoleEnums?.SUPER_ADMIN &&
          record?.role === UserRoleEnums?.SUPER_ADMIN
        ) {
          canEdit = true;
        } else if (
          loggedUserRole === UserRoleEnums?.SUPER_ADMIN &&
          filteredAdminAndUserData?.find((obj) => obj?.id === record?.id)
        ) {
          canDelete = true;
          canEdit = true;
          canUpdateStatus = true;
        } else if (
          loggedUserRole === UserRoleEnums?.ADMIN &&
          record?.id === localUserId
        ) {
          canEdit = true;
        } else if (
          loggedUserRole === UserRoleEnums?.ADMIN &&
          userRoleData?.find((obj) => obj?.id === record?.id)
        ) {
          canDelete = true;
          canEdit = true;
          canUpdateStatus = true;
        } else {
          canDelete = false;
          canEdit = false;
          canUpdateStatus = false;
        }
        return (
          <AdminActionsDropdown
            record={record}
            editBool={canEdit}
            deleteBool={canDelete}
            statusBool={canUpdateStatus}
            willReceiveUpdateBool={canUpdateStatus}
            viewBool={true}
            handleView={showDrawer}
            handleDeleteClick={deleteUser}
            handleEditClick={handleEditClick}
            handleUpdateStatusClick={handleUpdateStatusClick}
            handleUpdateWillReceiveClick={handleUpdateWillReceiveClick}
            registrationHistoryBool={true}
            handleRegistrationHistoryClick={handleRegistrationHistoryClick}
            text='Member Attendee'
            resource='member_attendees'
          />
        );
      },
    },
  ];
  columns.forEach((column) => {
    if (column.key !== 'action') {
      column.sortOrder =
        sortParams.field === column.dataIndex && sortParams.order;
    }
  });

  const formatCsvDateTime = (value) =>
    value ? dayjs(value).format('YYYY-MM-DD hh:mm A') : 'NA';

  const formateUsersData = () => {
    let newAllUsersData = [];
    allUsersDataWithoutPagination?.map((item) => {
      const localChapterData = allChapters?.data?.chapters?.find(
        (obj) => obj.id === item?.local_chapter_id?.id,
      );
      const statusMap = {
        1: 'Active',
        0: 'In-Active',
      };
      let newUser = {
        id: item.id,
        first_name: item.first_name,
        last_name: item.last_name,
        company: item?.company,
        local_chapter: item?.local_chapter_id?.name || 'N/A',
        email: item.email,
        title: item.title,
        address: item?.address,
        city: item?.city,
        state: item?.state,
        postal_code: item?.postal_code,
        country: item.country,
        phone_number: item?.phone_number,
        fax_number: item?.fax_number || 'NA',
        notes: item?.notes || 'NA',
        status:
          statusMap[item?.userstatus] !== undefined
            ? statusMap[item?.userstatus]
            : item?.userstatus ?? 'NA',
        role: item?.role ?? 'NA',
        is_vendor_reps: item?.is_vendor_reps ? 'Yes' : 'No',
        join_date: formatCsvDateTime(item?.join_date),
        istermsaccepted: item?.istermsaccepted ? 'Yes' : 'No',
        termsacceptedtime: formatCsvDateTime(item?.termsacceptedtime),
        last_login: formatCsvDateTime(item?.last_login),
        last_email_opened_at: formatCsvDateTime(item?.last_email_opened_at),
        last_email_clicked_at: formatCsvDateTime(item?.last_email_clicked_at),
        will_receive_update: item?.will_receive_update !== false ? 'Yes' : 'No',
        mail_preference:
          item?.mail_preference?.length > 0
            ? item.mail_preference
                .map((mp) => mp?.name)
                .filter(Boolean)
                .join(', ')
            : 'NA',
        refund_credit: formatCurrencyByLocalChapter(
          item?.refund_credit,
          localChapterData,
          2,
          2,
        ),
      };
      newAllUsersData.push(newUser);
    });
    newAllUsersData.push({
      id: '',
      first_name: '',
      last_name: '',
      company: '',
      local_chapter: '',
      email: '',
      title: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      phone_number: '',
      fax_number: '',
      notes: '',
      status: '',
      role: '',
      is_vendor_reps: '',
      join_date: '',
      istermsaccepted: '',
      termsacceptedtime: '',
      last_login: '',
      last_email_opened_at: '',
      last_email_clicked_at: '',
      will_receive_update: '',
      mail_preference: '',
      refund_credit: '',
    });
    return newAllUsersData;
  };
  const formattedUserData = formateUsersData();
  const headers = [
    {label: 'Id', key: 'id'},
    {label: 'First_name', key: 'first_name'},
    {label: 'Last_name', key: 'last_name'},
    {label: 'Company', key: 'company'},
    {label: 'Local Chapter', key: 'local_chapter'},
    {label: 'Email', key: 'email'},
    {label: 'Job Title', key: 'title'},
    {label: 'Address', key: 'address'},
    {label: 'City', key: 'city'},
    {label: 'State', key: 'state'},
    {label: 'Postal Code', key: 'postal_code'},
    {label: 'Country', key: 'country'},
    {label: 'Phone Number', key: 'phone_number'},
    {label: 'Fax Number', key: 'fax_number'},
    {label: 'Notes', key: 'notes'},
    {label: 'Status', key: 'status'},
    {label: 'Role', key: 'role'},
    {label: 'Vendor Representative', key: 'is_vendor_reps'},
    {label: 'Join Date', key: 'join_date'},
    {label: 'Terms Accepted', key: 'istermsaccepted'},
    {label: 'Terms Accepted Time', key: 'termsacceptedtime'},
    {label: 'Last LogIn', key: 'last_login'},
    {label: 'Announcement Emails Last Opened', key: 'last_email_opened_at'},
    {label: 'Announcement Emails Last Clicked', key: 'last_email_clicked_at'},
    {label: 'Receive Emails', key: 'will_receive_update'},
    {label: 'Mail Preference', key: 'mail_preference'},
    {label: 'Refund Credit', key: 'refund_credit'},
  ];

  // debouncing the table & pagination changes to stop too many requests
  const debouncedHandlePaginationChange = useDebouncedCallback(
    handlePaginationChange,
    1000,
  );
  const debouncedTableChange = useDebouncedCallback(handleTableChange, 1000);

  return (
    <div className='main-container'>
      {' '}
      <CreditHistoryModal
        open={creditHistoryOpen}
        onClose={() => setCreditHistoryOpen(false)}
        userId={creditHistoryUser?.id}
        memberName={creditHistoryUser?.name}
        localChapterData={creditHistoryUser?.chapter}
        onUpdated={() => fetchUsers(searchValues)}
      />
      <AdminViewDetailsDrawer
        content={MemberAttendeesDrawerContent}
        visible={open}
        text={'Member Attendees'}
        onCloseDrawer={onCloseDrawer}
        isEditable={
          (loggedUserRole === UserRoleEnums?.SUPER_ADMIN &&
            openDrawerDetails?.id === localUserId) ||
          (loggedUserRole === UserRoleEnums?.SUPER_ADMIN &&
            filteredAdminAndUserData?.find(
              (obj) => obj?.id === openDrawerDetails?.id,
            )) ||
          (loggedUserRole === UserRoleEnums?.ADMIN &&
            openDrawerDetails?.id === localUserId) ||
          (loggedUserRole === UserRoleEnums?.ADMIN &&
            userRoleData?.find((obj) => obj?.id === openDrawerDetails?.id))
            ? true
            : false
        }
        editNavigate={`/admin/memberAttendees/update/${openDrawerDetails?.id}`}
      />
      <StatusUpdateModal
        data={recordData}
        visible={isModalVisible}
        onCancel={hideModal}
        showUpdateStatus={showUpdateStatus}
        handleChange={handleChange}
        handleUpdateJobStatus={handleUpdateJobStatus}
        selectionOptions={UserStatusOptions}
      />
      <StatusUpdateModal
        data={recordDataWillReceive}
        visible={isWillReceiveModalVisible}
        onCancel={hideWillReceiveModal}
        showUpdateStatus={showWillReceiveSelection}
        handleChange={handleWillReceiveChange}
        handleUpdateJobStatus={handleUpdateWillReceiveSubmit}
        selectionOptions={WillReceiveUpdateOptions}
        statusHeading={
          ApplicationString['usersComponent.updateWillReceiveModalTitle']
        }
      />
      <HeaderSearchCard
        resource='member_attendees'
        toggleClick={toggleExpand}
        buttonTitle={ApplicationString['usersComponent.button.addForm']}
        addClick={() => navigate('/admin/memberAttendees/add')}
        dataTestId='adminMemberAttendees-button-addMemberAttendees'
        permission={CREATE}
        expanded={expanded}
        extra={
          <ProtectedResource
            resource='member_attendees'
            permission='downloadCsv'>
            <>
              {csvDataLoading && !allUsersDataWithoutPagination && (
                <Button>Fetching users..</Button>
              )}
              {!csvDataLoading && !allUsersDataWithoutPagination?.length && (
                <Button
                  csvDataLoading
                  onClick={() => {
                    fetchUsersWithoutPagination(searchValues);
                  }}>
                  Load CSV Data
                </Button>
              )}
              {!csvDataLoading && allUsersDataWithoutPagination?.length > 0 && (
                <Button id='Users-button-downloadCsv'>
                  <CSVLink
                    data={formattedUserData}
                    headers={headers}
                    filename={'User_Report.csv'}>
                    Download CSV
                  </CSVLink>
                </Button>
              )}
            </>
          </ProtectedResource>
        }
      />
      {expanded && (
        <DynamicSearchForm
          columns={userFilters}
          chaptersOptions={chaptersOptions}
          onSearch={handleSearch}
          savedFilteredValues={searchValues}
          title={'users'}
        />
      )}
      <div className='listing-container'>
        <Table
          bordered
          className='admin-table'
          onChange={debouncedTableChange}
          columns={columns}
          dataSource={allUsersData}
          key={allUsersData?.map((item) => {
            item.id;
          })}
          sticky={true}
          scroll={{x: columns?.length * 140}}
          pagination={{
            onChange: debouncedHandlePaginationChange,
            current: paging.page,
            total: paging.totalCount,
            pageSize: ps || paging.pageCount,
            pageSizeOptions: PageSizeArray,
            showSizeChanger: true,
            showQuickJumper: true,
            onShowSizeChange: (page, pageSize) => {
              handlePaginationChange(page, pageSize);
            },
            showTotal: (total, range) => `Total ${total} items`,
            position: [PaginationPosition],
          }}
        />
      </div>
    </div>
  );
};

export default UserComponent;
